/**
 * Maps the gateway's network-wide usage rollup (GET /usage/network) onto the
 * shapes the UI renders: the per-SKU usage map detail pages overlay onto their
 * endpoints. All pure.
 *
 * Request COUNTS arrive already combined across the v1/v2 metric-definition
 * cutover. v2 re-classified requests, it did not redefine what one is: both
 * definitions select the same conceptual population off different columns, over
 * one trailing 30-day window, so the two are commensurable and the gateway adds
 * them. Nothing here picks a definition, and a platform total is therefore
 * exactly the sum of its endpoints' counts.
 *
 * RATES are not commensurable that way, and they still choose one definition:
 * uptime, latency, and failover live in `lib/benchmarks.ts` and are unaffected.
 */

import type { NetworkUsage, SkuUsage, UsageTrend } from '../data/analytics';
import { fmtReq } from './format';

/** Minimum requests (per window) for a usage figure to display: show any real
 *  traffic, including smoke and test runs. A value of 0 or a missing record
 *  still reads as a dash, so columns and layout stay intact. */
export const USAGE_DISPLAY_MIN = 1;

/**
 * Percent change between two counts, one decimal. A start from zero is +100%.
 *
 * WARNING: that +100 is a placeholder for "undefined", not a measurement. It
 * must never be surfaced as a trend - callers reject `prev === 0` first, which
 * is what `weeklyTrend` does. Zero-filled buckets fed straight into this
 * function is how the public /usage page once claimed "100% vs prior 4 wks".
 */
export const pctChange = (prev: number, last: number): number => {
  if (prev > 0) return Math.round(((last - prev) / prev) * 1000) / 10;
  return last > 0 ? 100 : 0;
};

const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);

/**
 * Percent change over the widest EQUAL-WIDTH window the series supports, up to
 * `max` buckets a side.
 *
 * Equal width is the point: comparing 2 buckets against 4 returns a
 * plausible-looking number that means nothing. The label is generated from the
 * width actually used, so it can never claim "4 wks" while comparing 1. Returns
 * null with fewer than two buckets, or when the earlier half sums to zero -
 * there is no baseline to divide by, and `pctChange` would answer +100%.
 *
 * Both halves are buckets of the series rendered beside the number, and a zero
 * bucket now means the network genuinely had no traffic that week rather than
 * that a filter hid it. A young network therefore has a small baseline and can
 * legitimately report a large percentage; that is the data, not an artifact -
 * so the label states the baseline the percentage divided by, `vs prior 4 wks
 * (6,624 req)`. It is built from the same `prev` that `pctChange` receives, in
 * the same expression, so the two cannot drift apart.
 */
export const weeklyTrend = (values: number[], max = 4): UsageTrend | null => {
  const weeks = Math.min(max, Math.floor(values.length / 2));
  if (weeks < 1) return null;
  const prev = sum(values.slice(-2 * weeks, -weeks));
  if (prev === 0) return null;
  return {
    pct: pctChange(prev, sum(values.slice(-weeks))),
    weeks,
    // The `\u00a0` is a no-break space, written as an escape so it is visible in
    // source. It keeps the parenthetical whole: this label wraps inside the
    // pulse tile (a quarter of a 1180px page), and an ordinary space there
    // breaks after `(6,624` and strands `req)` alone on the second line.
    label: `vs prior ${weeks} ${weeks === 1 ? 'wk' : 'wks'} (${fmtReq(prev)}\u00a0req)`,
  };
};

/** Display-ready per-SKU traffic keyed by slug. A straight read of the rollup:
 *  the counts are already combined, so a SKU absent from `byApi` had no traffic
 *  in the window and correctly renders a dash. */
export const toSkuUsage = (n: Pick<NetworkUsage, 'byApi'>): Record<string, SkuUsage> => {
  const map: Record<string, SkuUsage> = {};
  for (const row of n.byApi) {
    map[row.sku] = {
      requests: row.requests,
      trendPct: weeklyTrend(row.weekly)?.pct ?? null,
      weekly: row.weekly,
    };
  }
  return map;
};

/** A SKU's displayable request figure: real count once it clears the noise
 *  floor, otherwise a quiet dash. Reads only the count, so it works on any row
 *  shape carrying one. */
export const fmtSkuReq = (u: { requests: number } | undefined): string =>
  u && u.requests >= USAGE_DISPLAY_MIN ? fmtReq(u.requests) : '-';

/** Whether a SKU has enough real traffic for its COUNT to be worth showing. */
export const hasUsageSignal = <T extends { requests: number } = { requests: number }>(
  u: T | undefined,
): u is T => !!u && u.requests >= USAGE_DISPLAY_MIN;

/** Whether a SKU's series can carry a sparkline and a trend arrow: it needs real
 *  traffic AND a comparable window, which a series of one or two buckets does
 *  not have. Separate from `hasUsageSignal` because a count can display without
 *  a direction to point in. */
export const canChartUsage = (
  u: SkuUsage | undefined,
): u is SkuUsage & { trendPct: number } => hasUsageSignal(u) && u.trendPct !== null;
